import * as Sentry from '@sentry/react';
import { Check, Copy } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { reloadIfStaleBuild } from '@/lib/build-check';
import { guardedReload, isChunkLoadError } from '@/lib/chunk-reload';

/**
 * Accent-tinted chip that houses the state's mark (the broken-thread glyph for
 * a real error, the spinning ring while a new build is loading). Shared so
 * both states read as the same "brand", just with different content inside.
 */
function MarkChip({ children }: { children: ReactNode }) {
  return (
    <div className="error-anim-rise-1 mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent text-primary">
      {children}
    </div>
  );
}

/**
 * Custom line-art mark: two capsule-shaped segments of a single "thread" that
 * have drifted apart, with a small pulse sitting exactly in the gap — a calm
 * "disconnected, but still here" motif rather than a stock warning triangle.
 */
function BrokenThreadMark() {
  return (
    <svg aria-hidden="true" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
      <rect height="3" rx="1.5" transform="rotate(-38 7.5 12)" width="8" x="3.5" y="10.5" />
      <rect height="3" rx="1.5" transform="rotate(-38 16.5 12)" width="8" x="12.5" y="10.5" />
      <circle cx="12" cy="12" fill="currentColor" r="1.3" stroke="none" />
    </svg>
  );
}

/**
 * Gentle spinning ring used as the chunk-update mark. With no `animation`
 * rule applying under prefers-reduced-motion (see index.css), it renders as
 * a plain static ring instead.
 */
function UpdatingRing() {
  return (
    <div
      aria-hidden="true"
      className="error-anim-ring-spin h-7 w-7 rounded-full border-2 border-primary/25 border-t-primary"
    />
  );
}

/** Monospaced "Reference: {eventId}" pill with a copy-to-clipboard affordance. */
function ReferencePill({ eventId }: { eventId: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(eventId)
      .then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard unavailable (e.g. insecure context) — the id is still visible to copy by hand.
      });
  };

  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/50 py-1 pl-3 pr-1.5 text-xs text-muted-foreground">
      <span>
        {t('common.reference')}: <span className="font-mono text-foreground">{eventId}</span>
      </span>
      <button
        aria-label={t('common.copy')}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-accent hover:text-accent-foreground"
        onClick={handleCopy}
        type="button"
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

/**
 * Shared shell for both ErrorBoundary states: a centered card floating over a
 * soft radial accent glow, with content staggered in via CSS-only motion
 * (skipped entirely under prefers-reduced-motion — see index.css). Callers
 * supply the mark, copy, and actions; the backdrop, card chrome, and entrance
 * motion live here once so the two states can never visually drift apart.
 *
 * Deliberately router-free: `AppErrorBoundary` sits above `<App />`'s own
 * `BrowserRouter` (see main.tsx), so an error caught here has already
 * unmounted that router — this shell (and its "Back to dashboard" link) must
 * not depend on router context.
 */
function ErrorScreen({
  mark,
  headline,
  body,
  actions,
  reference,
}: {
  mark: ReactNode;
  headline: string;
  body: string;
  actions: ReactNode;
  reference?: ReactNode;
}) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background p-6"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-1/2 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/10 blur-3xl"
      />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-xl">
        <MarkChip>{mark}</MarkChip>
        <h1 className="error-anim-rise-2 text-2xl font-bold text-foreground">{headline}</h1>
        <p className="error-anim-rise-2 mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
        <div className="error-anim-rise-3 mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
          {actions}
        </div>
        {reference ? <div className="error-anim-rise-3 mt-5 flex justify-center">{reference}</div> : null}
      </div>
    </div>
  );
}

/**
 * Fallback shown when the ErrorBoundary catches a stale-chunk error (a lazy route
 * whose content-hashed chunk no longer exists because a new build was deployed).
 * `guardedReload()` already fired (see `onError` below); this UI covers the brief
 * moment before that reload lands, and gives the user a manual way out if the
 * reload was suppressed by the loop guard (e.g. the reload already happened once
 * in the last 10s and didn't fix it).
 */
function ChunkUpdateFallback() {
  const { t } = useTranslation();
  return (
    <ErrorScreen
      actions={<Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>}
      body={t('common.chunk_update_body')}
      headline={t('common.updating_to_latest_version')}
      mark={<UpdatingRing />}
    />
  );
}

/** Fallback shown for a real (non-chunk) application error caught by the boundary. */
function GenericErrorScreen({ eventId, fallbackMessage }: { eventId: string; fallbackMessage: string }) {
  const { t } = useTranslation();
  return (
    <ErrorScreen
      actions={
        <>
          <Button onClick={() => window.location.reload()}>{t('common.reload')}</Button>
          <Button asChild variant="ghost">
            <a href="/">{t('common.back_to_dashboard')}</a>
          </Button>
        </>
      }
      body={t('common.error_body')}
      headline={fallbackMessage}
      mark={<BrokenThreadMark />}
      reference={eventId ? <ReferencePill eventId={eventId} /> : undefined}
    />
  );
}

export function AppErrorBoundary({ children, fallbackMessage }: { children: ReactNode; fallbackMessage: string }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, eventId }) =>
        isChunkLoadError(error) ? (
          <ChunkUpdateFallback />
        ) : (
          <GenericErrorScreen eventId={eventId} fallbackMessage={fallbackMessage} />
        )
      }
      beforeCapture={(scope, error) => {
        // Chunk-load errors are expected (a stale tab after a deploy). Recovery is attempted
        // here rather than in `onError` because Sentry captures before `onError` runs: the
        // reload has to be under way by now for `beforeSend` to know this one healed itself.
        // This also covers chunk errors that arrive as a thrown error instead of a
        // `vite:preloadError` event. A second call inside the cooldown is a no-op.
        if (isChunkLoadError(error)) {
          scope.setTag('chunk_load_error', true);
          scope.setLevel('info');
          guardedReload();
          return;
        }

        // Not a missing chunk, so the tab may still be running superseded code: `/assets/` is
        // cached immutable, and an old lazy chunk loads from that cache without ever 404ing.
        // Asking the server costs a request and reloads only on proof, so an ordinary bug still
        // reaches the screen below — and is still reported, because the capture is already under
        // way by the time the answer arrives.
        void reloadIfStaleBuild();
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
